import React, { Suspense, useState, useEffect } from "react";
import Amplify, { Auth, Hub } from "aws-amplify";
import * as Sentry from "@sentry/browser";

import { Switch, Route, Redirect } from "react-router-dom";

import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import "./App.scss";

import api from "./libs/api";
import Tracking from "@magic-engineering/magic-tracking";

import Loading from "./components/Loading/Loading";

import DashboardWrapper from "./components/Dashboard/DashboardWrapper";
import EditProfile from "./components/Profile/EditProfile";
import Navbar from "./components/Navbar/Navbar";
import Bill from "./components/Billing/Bill.lazy";
import Sidebar from "./components/Sidebar/Sidebar";
import JobRequestPage from "./pages/JobRequestPage/JobRequestPage.lazy";
import HireAssistantPage from "./pages/HireAssistantPage/HireAssistantPage.lazy";
import EditPlanPage from "./pages/EditPlanPage/EditPlanPage.lazy";
import Authenticator from "./components/Authentication/Authenticator.js";
import ForgotPasswordPage from "./pages/ForgotPasswordPage/ForgotPasswordPage.lazy";

import AccountSettingsPage from "./pages/AccountSettingsPage/AccountSettingsPage.lazy.js";
import FeedbackPage from "./pages/FeedbackPage/FeedbackPage.lazy.js";
import CreditCardPage from "./pages/CreditCardPage/CreditCardPage.lazy.js";
import ConfirmJobRequestPage from "./pages/ConfirmJobRequestPage/ConfirmJobRequestPage.lazy.js";
import NotificationSettingsPage from "./pages/NotificationSettingsPage/NotificationSettingsPage.lazy.js";
import UnsubscribePage from "./pages/UnsubscribePage/UnsubscribePage.lazy.js";
import ClientDeposit from "./pages/ClientDepositPage/ClientDepositPage.lazy.js";

import { useAssistants, setAssistants } from "./redux/assistants";
import { useClient, setClient } from "./redux/client";
import FeatureFlagPage from "./pages/FeatureFlagPage/FeatureFlagPage";
import { useLocation } from "react-router-dom";
import NotFoundPage from "./pages/NotFoundPage/NotFoundPage";

const AssistantCancellation = React.lazy(() =>
  import("./components/AssistantCancellation/AssistantCancellation.js")
);

const config = {
  aws_project_region: process.env.REACT_APP_AWS_PROJECT_REGION,
  aws_cognito_identity_pool_id: process.env.REACT_APP_AWS_COGNITO_IDENTITY_POOL_ID_CLIENT_PORTAL,
  aws_cognito_region: process.env.REACT_APP_AWS_COGNITO_REGION,
  aws_user_pools_id: process.env.REACT_APP_AWS_USER_POOLS_ID_CLIENT_PORTAL,
  aws_user_pools_web_client_id: process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID_CLIENT_PORTAL,
  oauth: {},
};

Amplify.configure(config);
Auth.configure(config);

/**
 * Wrapper to initialize sentry and tracking library
 */
function trackUser(user) {
  Tracking.initialize(user, {
    googleAnalytics: {
      measurementId: process.env.REACT_APP_GA_MEASUREMENT_ID_CLIENT_PORTAL,
    },
    rudderStack: {
      configuredDestinations: {
        All: process.env.REACT_APP_DEBUG_ANALYTICS || process.env.REACT_APP_STAGE === "prod",
      },
    },
  });

  try {
    const { attributes, username } = user;
    const email = attributes.email;
    const first_name = attributes["custom:first_name"];
    const last_name = attributes["custom:last_name"];

    Sentry.setUser({
      email,
      username: `${first_name} ${last_name}`,
      id: username,
    });
  } catch (e) {
    console.warn("Failed to set sentry user");
  }
}

function App() {
  const assistants = useAssistants();
  const client = useClient();
  const [hasUser, setHasUser] = useState(false);
  const [fetchedAssistants, setFetchedAssistants] = useState(false);
  const location = useLocation();
  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        setClient(user);
        setHasUser(true);
        if (user) {
          trackUser(user);
        }
      })
      .catch((error) => {
        Sentry.withScope((scope) => {
          scope.setLevel("warning");
          Sentry.captureMessage(error);
        });
        setHasUser(false);
        Tracking.initialize(undefined);
      });

    Hub.listen("auth", (data) => {
      const { event, data: cognitoData } = (data && data.payload) || {};

      switch (event) {
        case "signIn":
          setClient(cognitoData);
          setHasUser(true);
          if (cognitoData) {
            trackUser(cognitoData);
          }
          break;
        case "signOut":
          setClient(null);
          setHasUser(false);
          break;
        default:
          break;
      }
    });
  }, []);

  useEffect(() => {
    async function fetchAssistants() {
      const { data } = await api.get("/assistants");
      setAssistants(data || []);
      setFetchedAssistants(true);
    }

    if (hasUser && !fetchedAssistants) {
      fetchAssistants();
    }
  }, [fetchedAssistants, hasUser]);

  const refetchAssistants = () => setFetchedAssistants(false);

  const shouldShowSidebar = () => hasUser && location.pathname !== "/client-deposit";
  return (
    <div className="App" data-testid="AppId">
      <Navbar {...{ client, hasUser }} />
      <div className="d-flex">
        {shouldShowSidebar() && <Sidebar />}
        <div className="d-flex flex-column w-100">
          <div className="content-fluid w-100">
            <Switch>
              {hasUser && (
                <Route exact path="/profile/settings/notifications">
                  <NotificationSettingsPage />
                </Route>
              )}
            </Switch>
          </div>
          <div className="content w-100">
            <Switch>
              {/* Log In is OPTIONAL routes.
                User available or undefined.
                Component must handle undefined client */}
              <Route exact path="/enable-feature-flag">
                <FeatureFlagPage enable={true} />
              </Route>
              <Route exact path="/disable-feature-flag">
                <FeatureFlagPage enable={false} />
              </Route>
              {/* /jobask: also utilizes query ?search_value */}
              <Route exact path="/jobask/:assistant_url">
                <JobRequestPage client={client} />
              </Route>
              <Route exact path="/unsubscribe/:url">
                <UnsubscribePage />
              </Route>
              <Route exact path="/forgot-password">
                <ForgotPasswordPage />
              </Route>
              <Route exact path="/hire/:url">
                <HireAssistantPage
                  isLoggedIn={hasUser}
                  onSuccess={hasUser ? refetchAssistants : null}
                />
              </Route>
              {/* Logged Out state REQUIRED (user not available) */}
              {!hasUser && (
                <Route>
                  <Authenticator />
                </Route>
              )}
              {hasUser && (
                // Logged In state REQUIRED routes (user available)
                <>
                  <Route exact path="/">
                    <DashboardWrapper
                      assistants={assistants}
                      client={client}
                      fetchedAssistants={fetchedAssistants}
                    />
                  </Route>
                  <Route exact path="/profile/edit">
                    <EditProfile client={client} />
                  </Route>
                  <Route exact path="/profile/settings/account">
                    <AccountSettingsPage />
                  </Route>
                  <Route exact path="/plan/:planId">
                    <EditPlanPage assistants={assistants} onSuccess={refetchAssistants} />
                  </Route>
                  <Route path="/bill/:billId">
                    <Bill />
                  </Route>
                  <Route path="/404">
                    <NotFoundPage />
                  </Route>
                  <Route exact path="/feedbacks/:assistantUserId">
                    <FeedbackPage client={client} />
                  </Route>
                  <Route path="/registration">
                    <Redirect to="/" />
                  </Route>
                  <Route path="/checkout">
                    <Redirect to="/payment-info" />
                  </Route>
                  <Route path="/confirm-request">
                    <ConfirmJobRequestPage onSuccess={refetchAssistants} />
                  </Route>
                  <Route path="/stop-assistant-work">
                    <Suspense fallback={<Loading />}>
                      <AssistantCancellation />
                    </Suspense>
                  </Route>
                  <Route path="/payment-info">
                    <CreditCardPage />
                  </Route>
                  <Route path="/client-deposit">
                    <ClientDeposit />
                  </Route>
                </>
              )}
            </Switch>
          </div>
        </div>
      </div>
      <ToastContainer position="bottom-right" />
    </div>
  );
}

export default App;
